import { useMemo } from 'react';

import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { createColumnHelper } from '@tanstack/react-table';

import DateFilter from '../../trafficking/components/filters/DateFilter';
import PacingMetricsStatusFilter from '../components/diagnostics/PacingMetricsStatusFilter';
import PacingMetricStatusChip from '../components/diagnostics/PacingMetricStatusChip';
import TrendSparkLineCell from '../components/diagnostics/TrendSparkLineCell';
import { CampaignDiagnosticMetric } from '../types';

import { usePlatform } from '@/features/users';
import { Info } from '@/shared/components/icons';
import { Tooltip } from '@/shared/components/tooltip';

const columnHelper = createColumnHelper<CampaignDiagnosticMetric>();

const useCampaignDiagnosticsColumns = () => {
  const platform = usePlatform();
  const isDV360 = platform === 'DV360';
  const isMeta = platform === 'Meta';

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'nameAndId',
        header: () => (
          <Typography variant='body3' component='span'>
            {isDV360 ? 'Insertion Order Name and ID' : 'Campaign Name and ID'}
          </Typography>
        ),

        cell: (props) => {
          const row = props.row.original;
          const displayName = isDV360
            ? (row.ioName ?? 'N/A')
            : row.campaignName;
          const displayId = isDV360 ? row.ioId : row.campaignId;

          return (
            <Stack gap={0.75}>
              <Typography
                variant='subtitleLabel'
                component='span'
                whiteSpace='break-spaces'
                sx={{ wordBreak: 'break-word' }}
                color='primary'
              >
                {displayName}
              </Typography>
              {displayId && (
                <Typography
                  variant='subtitleLabel'
                  component='span'
                  color='primary'
                >
                  ({displayId})
                </Typography>
              )}
            </Stack>
          );
        },

        size: 300,
      }),

      columnHelper.accessor('algorithmStatus', {
        header: () => (
          <Stack direction='row' alignItems='center' gap={0.5}>
            <Tooltip
              title='Indicates performance of starvation bidder algorithm'
              placement='top'
            >
              <Info sx={{ color: 'text.secondary', fontSize: 16 }} />
            </Tooltip>
            <Typography variant='body3' component='span'>
              Algorithm Phase
            </Typography>
          </Stack>
        ),

        cell: (props) => (
          <PacingMetricStatusChip
            starvationBidderApplied={props.row.original.starvationBidderApplied}
            status={props.getValue()}
          />
        ),

        size: 180,
        enableSorting: false,
        meta: {
          FilterComponentFn: (_column) => {
            const searchParams = useSearch({
              from: '/_app/algorithm-tools/starvation-bidder',
            });
            const navigate = useNavigate({
              from: '/algorithm-tools/starvation-bidder',
            });

            return (
              <PacingMetricsStatusFilter
                filterId={'pacingMetricsStatusFilter'}
                buttonAriaLabel={'Pacing Metrics Status Filter'}
                serachParamStatuses={searchParams.algorithmStatus}
                handleNavigation={(statuses) => {
                  void navigate({
                    search: {
                      ...searchParams,
                      algorithmStatus: statuses,
                      page: 1,
                    },
                    resetScroll: false,
                  });
                }}
              />
            );
          },
        },
      }),

      ...(!isDV360
        ? [
            columnHelper.accessor('campaignStartDate', {
              enableSorting: false,
              header: () => (
                <Typography variant='body3' component='span'>
                  Start Date
                </Typography>
              ),
              cell: (props) =>
                props.getValue() ? (
                  <Typography
                    variant='subtitleNumbers'
                    component='span'
                    color='text.tertiary'
                  >
                    {new Date(props.getValue()).toLocaleDateString()}
                  </Typography>
                ) : (
                  <Typography
                    variant='subtitleLabel'
                    component='span'
                    color='text.secondary'
                  >
                    N/A
                  </Typography>
                ),
              size: 120,
              meta: {
                FilterComponentFn: (_column) => {
                  const searchParams = useSearch({
                    from: '/_app/algorithm-tools/starvation-bidder',
                  });
                  const navigate = useNavigate({
                    from: '/algorithm-tools/starvation-bidder',
                  });

                  return (
                    <DateFilter
                      filterId='campaignStartDateFilter'
                      buttonAriaLabel='campaign start date filter'
                      searchParamStringDate={searchParams.campaignStartDate}
                      handleNavigation={(newDate) => {
                        void navigate({
                          search: {
                            ...searchParams,
                            campaignStartDate: newDate,
                            page: 1,
                          },
                          resetScroll: false,
                        });
                      }}
                    />
                  );
                },
              },
            }),

            columnHelper.accessor('campaignEndDate', {
              enableSorting: false,
              header: () => (
                <Typography variant='body3' component='span'>
                  End Date
                </Typography>
              ),
              cell: (props) =>
                props.getValue() ? (
                  <Typography
                    variant='subtitleNumbers'
                    component='span'
                    color='text.tertiary'
                  >
                    {new Date(props.getValue()).toLocaleDateString()}
                  </Typography>
                ) : (
                  <Typography
                    variant='subtitleLabel'
                    component='span'
                    color='text.secondary'
                  >
                    N/A
                  </Typography>
                ),
              size: 120,
              meta: {
                FilterComponentFn: (_column) => {
                  const searchParams = useSearch({
                    from: '/_app/algorithm-tools/starvation-bidder',
                  });
                  const navigate = useNavigate({
                    from: '/algorithm-tools/starvation-bidder',
                  });

                  return (
                    <DateFilter
                      filterId='campaignEndDateFilter'
                      buttonAriaLabel='campaign end date filter'
                      searchParamStringDate={searchParams.campaignEndDate}
                      handleNavigation={(newDate) => {
                        void navigate({
                          search: {
                            ...searchParams,
                            campaignEndDate: newDate,
                            page: 1,
                          },
                          resetScroll: false,
                        });
                      }}
                    />
                  );
                },
              },
            }),
          ]
        : []),

      ...(isDV360
        ? [
            columnHelper.accessor('ioStartDate', {
              enableSorting: false,
              header: () => (
                <Typography variant='body3' component='span'>
                  Start Date
                </Typography>
              ),
              cell: (props) => {
                const val = props.getValue();
                return val ? (
                  <Typography
                    variant='subtitleNumbers'
                    component='span'
                    color='text.tertiary'
                  >
                    {new Date(val).toLocaleDateString()}
                  </Typography>
                ) : (
                  <Typography
                    variant='subtitleLabel'
                    component='span'
                    color='text.secondary'
                  >
                    N/A
                  </Typography>
                );
              },
              size: 120,
              meta: {
                FilterComponentFn: (_column) => {
                  const searchParams = useSearch({
                    from: '/_app/algorithm-tools/starvation-bidder',
                  });
                  const navigate = useNavigate({
                    from: '/algorithm-tools/starvation-bidder',
                  });

                  return (
                    <DateFilter
                      filterId='ioStartDateFilter'
                      buttonAriaLabel='IO start date filter'
                      searchParamStringDate={searchParams.ioStartDate}
                      handleNavigation={(newDate) => {
                        void navigate({
                          search: {
                            ...searchParams,
                            ioStartDate: newDate,
                            page: 1,
                          },
                          resetScroll: false,
                        });
                      }}
                    />
                  );
                },
              },
            }),

            columnHelper.accessor('ioEndDate', {
              enableSorting: false,
              header: () => (
                <Typography variant='body3' component='span'>
                  End Date
                </Typography>
              ),
              cell: (props) => {
                const val = props.getValue();
                return val ? (
                  <Typography
                    variant='subtitleNumbers'
                    component='span'
                    color='text.tertiary'
                  >
                    {new Date(val).toLocaleDateString()}
                  </Typography>
                ) : (
                  <Typography
                    variant='subtitleLabel'
                    component='span'
                    color='text.secondary'
                  >
                    N/A
                  </Typography>
                );
              },
              size: 120,
              meta: {
                FilterComponentFn: (_column) => {
                  const searchParams = useSearch({
                    from: '/_app/algorithm-tools/starvation-bidder',
                  });
                  const navigate = useNavigate({
                    from: '/algorithm-tools/starvation-bidder',
                  });

                  return (
                    <DateFilter
                      filterId='ioEndDateFilter'
                      buttonAriaLabel='IO end date filter'
                      searchParamStringDate={searchParams.ioEndDate}
                      handleNavigation={(newDate) => {
                        void navigate({
                          search: {
                            ...searchParams,
                            ioEndDate: newDate,
                            page: 1,
                          },
                          resetScroll: false,
                        });
                      }}
                    />
                  );
                },
              },
            }),
          ]
        : []),

      columnHelper.accessor('kpiType', {
        header: () => (
          <Typography variant='body3' component='span'>
            KPI Type
          </Typography>
        ),

        cell: (props) => (
          <Typography variant='subtitleLabel' component='span'>
            {props.getValue()}
          </Typography>
        ),
        size: 100,
        enableSorting: false,
      }),

      columnHelper.accessor('kpiGoal', {
        header: () => (
          <Typography variant='body3' component='span'>
            KPI Goal
          </Typography>
        ),

        cell: (props) => (
          <Typography variant='subtitleLabel' component='span'>
            {props.getValue()}
          </Typography>
        ),
        size: 100,
        enableSorting: false,
      }),

      columnHelper.accessor('kpiActual.value', {
        header: () => (
          <Typography variant='body3' component='span'>
            KPI Actual
          </Typography>
        ),

        cell: (props) => (
          <Typography variant='subtitleLabel' component='span'>
            {new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
              maximumFractionDigits: 2,
            }).format(props.getValue())}
          </Typography>
        ),
        size: 100,
        enableSorting: false,
      }),

      columnHelper.accessor('kpiTrends', {
        header: () => (
          <Stack direction='row' alignItems='center' gap={0.5}>
            <Tooltip title='Over the past 7 days' placement='top'>
              <Info sx={{ color: 'text.secondary', fontSize: 16 }} />
            </Tooltip>
            <Typography variant='body3' component='span'>
              KPI Trend
            </Typography>
          </Stack>
        ),

        cell: (props) => <TrendSparkLineCell trends={props.getValue()} />,

        size: 150,
        enableSorting: false,
      }),

      ...(!isMeta
        ? [
            columnHelper.accessor('cpm.value', {
              header: () => (
                <Typography variant='body3' component='span'>
                  CPM
                </Typography>
              ),

              cell: (props) => (
                <Typography variant='subtitleLabel' component='span'>
                  {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    maximumFractionDigits: 2,
                  }).format(props.getValue())}
                </Typography>
              ),

              size: 80,
              enableSorting: false,
            }),

            columnHelper.accessor('cpmTrends', {
              header: () => (
                <Stack direction='row' alignItems='center' gap={0.5}>
                  <Tooltip title='Over the past 7 days' placement='top'>
                    <Info sx={{ color: 'text.secondary', fontSize: 16 }} />
                  </Tooltip>
                  <Typography variant='body3' component='span'>
                    CPM Trend
                  </Typography>
                </Stack>
              ),

              cell: (props) => <TrendSparkLineCell trends={props.getValue()} />,
              size: 150,
              enableSorting: false,
            }),
          ]
        : []),

      columnHelper.accessor('pacingPercentage', {
        header: () => (
          <Typography variant='body3' component='span'>
            Pacing %
          </Typography>
        ),

        cell: (props) =>
          props.getValue() != null ? (
            <Typography variant='subtitleLabel' component='span'>
              {new Intl.NumberFormat('en-US', {
                style: 'percent',
                maximumFractionDigits: 0,
              }).format(props.getValue() ?? 0)}
            </Typography>
          ) : (
            <Typography
              variant='subtitleLabel'
              component='span'
              color='text.secondary'
            >
              N/A
            </Typography>
          ),

        size: 100,
        enableSorting: false,
      }),

      columnHelper.accessor('percentageThroughFlight', {
        header: () => (
          <Typography variant='body3' component='span'>
            % Through Flight
          </Typography>
        ),

        cell: (props) =>
          props.getValue() != null ? (
            <Typography variant='subtitleLabel' component='span'>
              {new Intl.NumberFormat('en-US', {
                style: 'percent',
                maximumFractionDigits: 0,
              }).format((props.getValue() ?? 0) / 100)}
            </Typography>
          ) : (
            <Typography
              variant='subtitleLabel'
              component='span'
              color='text.secondary'
            >
              N/A
            </Typography>
          ),

        size: 150,
        enableSorting: false,
      }),

      columnHelper.accessor('budget', {
        header: () => (
          <Typography variant='body3' component='span'>
            Budget
          </Typography>
        ),

        cell: (props) => (
          <Typography variant='subtitleLabel' component='span'>
            {new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
              maximumFractionDigits: 0,
            }).format(props.getValue())}
          </Typography>
        ),

        size: 110,
      }),

      columnHelper.accessor('totalSpend', {
        header: () => (
          <Typography variant='body3' component='span'>
            Total Spend
          </Typography>
        ),

        cell: (props) => (
          <Typography variant='subtitleLabel' component='span'>
            {new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
              maximumFractionDigits: 0,
            }).format(props.getValue())}
          </Typography>
        ),

        size: 110,
      }),

      columnHelper.accessor('yesterdaySpend', {
        header: () => (
          <Typography variant='body3' component='span'>
            Yesterday&apos;s Spend
          </Typography>
        ),

        cell: (props) => (
          <Typography variant='subtitleLabel' component='span'>
            {new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
              maximumFractionDigits: 0,
            }).format(props.getValue())}
          </Typography>
        ),

        size: 150,
      }),

      columnHelper.accessor('neededSpendYesterday', {
        header: () => (
          <Typography variant='body3' component='span'>
            Needed Spend Yesterday
          </Typography>
        ),

        cell: (props) =>
          props.getValue() != null ? (
            <Typography variant='subtitleLabel' component='span'>
              {new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                maximumFractionDigits: 0,
              }).format(props.getValue() ?? 0)}
            </Typography>
          ) : (
            <Typography
              variant='subtitleLabel'
              component='span'
              color='text.secondary'
            >
              N/A
            </Typography>
          ),

        size: 200,
      }),
    ],
    [isDV360, isMeta]
  );

  return columns;
};

export default useCampaignDiagnosticsColumns;
